/**
 * PlaybookDetail — safety cockpit: arm switch, action sequence, attached alarms,
 * and audit panel (triggering incidents as proxy).
 * Attach alarm: dropdown of unattached project alarms → PATCH /alarms/:id/playbook.
 * Detach: [×] button → PATCH {playbook_id: null}.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Bell, Workflow, X } from 'lucide-react';
import { Breadcrumbs, BreadcrumbItem, Container, PageHeader } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import {
  Button,
  Badge,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  CardDescription,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  usePlaybook,
  useArmPlaybook,
  useDeletePlaybook,
  useAlarms,
  useWatchpoints,
  useSetAlarmPlaybook,
  type Alarm,
} from '@/api/healthcheck';
import { hcExtractError } from '@/api/healthcheck';
import { severityTone } from '../shared';
import { ArmToggle } from './ArmToggle';
import { PlaybookSequence } from './PlaybookSequence';
import { PlaybookAuditPanel } from './PlaybookAuditPanel';
import { RowMenu } from '../../components/RowMenu';
import { PlaybookCreate } from './PlaybookCreate';
import styles from './PlaybookLayout.module.css';

export function PlaybookDetail() {
  const navigate = useNavigate();
  const { projectId, playbookId } = useParams<{ projectId: string; playbookId: string }>();
  const base = `/services/healthcheck/${projectId}`;
  const { show } = useToast();

  const { data: playbook, isLoading, isError, refetch } = usePlaybook(playbookId);
  const { data: alarmItems = [], isLoading: alarmsLoading } = useAlarms();
  const { data: wpData, isLoading: wpLoading } = useWatchpoints(projectId, {});

  const arm = useArmPlaybook(playbookId ?? '');
  const deletePlaybook = useDeletePlaybook({
    onSuccess: () => navigate(`${base}/playbooks`),
  });

  const projectWpIds = useMemo(
    () => new Set((wpData?.items ?? []).map((w) => w.id)),
    [wpData],
  );
  // Gate on wpData loaded: avoids a flash of [] while watchpoints are fetching,
  // which would make attached/available alarms briefly vanish then reappear.
  const projectAlarms = useMemo(
    () => (wpLoading ? [] : alarmItems.filter((a) => projectWpIds.has(a.watchpoint_id))),
    [alarmItems, projectWpIds, wpLoading],
  );

  const attachedAlarms = useMemo(
    () => projectAlarms.filter((a) => a.playbook_id === playbookId),
    [projectAlarms, playbookId],
  );

  const availableAlarms = useMemo(
    () => projectAlarms.filter((a) => !a.playbook_id),
    [projectAlarms],
  );

  const [pendingDelete, setPendingDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  if (isLoading) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 48, maxWidth: 400, marginBottom: 'var(--vn-space-4)' }} />
        <Skeleton style={{ height: 320 }} />
      </Container>
    );
  }

  if (isError || !playbook) {
    return (
      <Container size="xl">
        <Alert
          tone="danger"
          title="Couldn't load playbook"
          actions={
            <Button size="sm" variant="secondary" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      </Container>
    );
  }

  if (showEdit) {
    return <PlaybookCreate playbookId={playbookId} />;
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<Workflow size={22} />}
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem asChild><Link to={base}>Monitoring</Link></BreadcrumbItem>
            <BreadcrumbItem asChild><Link to={`${base}/playbooks`}>Playbooks</Link></BreadcrumbItem>
            <BreadcrumbItem current>{playbook.name}</BreadcrumbItem>
          </Breadcrumbs>
        }
        title={playbook.name}
        description={`Auto-mitigation runbook · ${playbook.actions.length} action${playbook.actions.length === 1 ? '' : 's'}`}
        actions={
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)' }}>
            <ArmToggle
              playbook={playbook}
              attachedAlarmCount={attachedAlarms.length}
              isPending={arm.isPending}
              onArm={() =>
                arm.mutate(true, {
                  onError: (e) => show({ tone: 'danger', title: hcExtractError(e, 'Arm failed') }),
                })
              }
              onDisarm={() =>
                arm.mutate(false, {
                  onError: (e) =>
                    show({ tone: 'danger', title: hcExtractError(e, 'Disarm failed') }),
                })
              }
            />
            <Button size="sm" variant="secondary" onClick={() => setShowEdit(true)}>
              Edit
            </Button>
            <RowMenu
              items={[
                { label: 'Delete', danger: true, onSelect: () => setPendingDelete(true) },
              ]}
            />
          </div>
        }
      />

      {playbook.is_active ? (
        <Alert tone="danger" title="ARMED — this playbook will automatically execute its actions when a linked alarm fires.">
          Disarm to pause without deleting.
        </Alert>
      ) : (
        <Alert tone="info" title="Disarmed — nothing runs against live traffic.">
          Arm this playbook from the switch above when you are ready to go live.
        </Alert>
      )}

      <div className={styles.twoCol} style={{ marginTop: 'var(--vn-space-4)' }}>
        {/* Left column */}
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <Card flat>
            <CardHeader>
              <CardTitle>Action sequence</CardTitle>
              <CardDescription>Executes top-to-bottom when a linked alarm fires.</CardDescription>
            </CardHeader>
            <CardBody>
              <PlaybookSequence actions={playbook.actions} />
            </CardBody>
          </Card>

          <Card flat>
            <CardHeader>
              <CardTitle>Run history</CardTitle>
            </CardHeader>
            <CardBody>
              <PlaybookAuditPanel
                attachedAlarms={attachedAlarms}
                isLoadingAlarms={alarmsLoading}
              />
            </CardBody>
          </Card>
        </div>

        {/* Right column — attached alarms */}
        <div style={{ position: 'sticky', top: 'var(--vn-space-6)' }}>
          <AttachedAlarmsPanel
            playbookId={playbookId!}
            attachedAlarms={attachedAlarms}
            availableAlarms={availableAlarms}
            isLoading={alarmsLoading}
          />
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(false)}
        destructive
        title={`Delete "${playbook.name}"?`}
        description="This playbook will be permanently deleted. Alarms linked to it will be detached."
        confirmLabel="Delete"
        onConfirm={() => {
          setPendingDelete(false);
          deletePlaybook.mutate(playbook.id, {
            onError: (e) =>
              show({ tone: 'danger', title: hcExtractError(e, 'Delete failed') }),
          });
        }}
      />
    </Container>
  );
}

// ---- Attached alarms panel -----------------------------------------------

interface AttachedAlarmsPanelProps {
  playbookId: string;
  attachedAlarms: Alarm[];
  availableAlarms: Alarm[];
  isLoading: boolean;
}

function AttachedAlarmsPanel({
  playbookId,
  attachedAlarms,
  availableAlarms,
  isLoading,
}: AttachedAlarmsPanelProps) {
  const { show } = useToast();

  if (isLoading) return <Skeleton style={{ height: 160 }} />;

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Attached alarms</CardTitle>
        <CardDescription>Alarms that trigger this runbook.</CardDescription>
      </CardHeader>
      <CardBody>
        <div style={{ display: 'grid', gap: 0 }}>
          {attachedAlarms.length === 0 && (
            <EmptyState
              icon={<Bell size={20} />}
              title="No alarms attached yet"
              description="Attach an alarm below to trigger this playbook automatically."
              size="sm"
            />
          )}

          {attachedAlarms.map((alarm) => (
            <AlarmRow
              key={alarm.id}
              alarm={alarm}
              onDetachError={(msg) => show({ tone: 'danger', title: msg })}
            />
          ))}

          {availableAlarms.length > 0 && (
            <AttachAlarmPicker
              playbookId={playbookId}
              available={availableAlarms}
              onError={(msg) => show({ tone: 'danger', title: msg })}
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// ---- Single attached alarm row -------------------------------------------

function AlarmRow({
  alarm,
  onDetachError,
}: {
  alarm: Alarm;
  onDetachError: (msg: string) => void;
}) {
  const detach = useSetAlarmPlaybook();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--vn-space-2)',
        padding: 'var(--vn-space-2) 0',
        borderBottom: '1px solid var(--vn-border-default)',
        fontSize: 'var(--vn-font-size-sm)',
      }}
    >
      <span
        style={{
          flex: 1,
          fontWeight: 'var(--vn-font-weight-medium)' as React.CSSProperties['fontWeight'],
        }}
      >
        {alarm.name}
      </span>
      <Badge
        tone={severityTone(alarm.severity)}
        appearance="subtle"
        size="sm"
        style={{ textTransform: 'capitalize' }}
      >
        {alarm.severity}
      </Badge>
      <Button
        size="sm"
        variant="ghost"
        aria-label={`Detach ${alarm.name}`}
        disabled={detach.isPending}
        onClick={() =>
          detach.mutate({ alarmId: alarm.id, playbookId: null }, {
            onError: (e) => onDetachError(hcExtractError(e, 'Detach failed')),
          })
        }
        style={{ padding: '0 var(--vn-space-1)', color: 'var(--vn-fg-muted)' }}
      >
        <X size={13} />
      </Button>
    </div>
  );
}

// ---- Attach alarm picker -------------------------------------------------

function AttachAlarmPicker({
  playbookId,
  available,
  onError,
}: {
  playbookId: string;
  available: Alarm[];
  onError: (msg: string) => void;
}) {
  const [selectedId, setSelectedId] = useState('');
  // Hook is parameterless — alarmId is passed atomically at mutate() time to avoid
  // stale-closure bugs (state update is async; hook arg would lag one render).
  const attach = useSetAlarmPlaybook();

  const handleSelect = (alarmId: string) => {
    setSelectedId(alarmId);
    attach.mutate({ alarmId, playbookId }, {
      onSuccess: () => setSelectedId(''),
      onError: (e) => {
        onError(hcExtractError(e, 'Attach failed'));
        setSelectedId('');
      },
    });
  };

  return (
    <div style={{ marginTop: 'var(--vn-space-3)' }}>
      <Select
        value={selectedId}
        onValueChange={handleSelect}
        disabled={attach.isPending}
      >
        <SelectTrigger>
          <SelectValue placeholder="+ Attach alarm…" />
        </SelectTrigger>
        <SelectContent>
          {available.map((alarm) => (
            <SelectItem key={alarm.id} value={alarm.id}>
              {alarm.name}{' '}
              <span
                style={{
                  color: 'var(--vn-fg-muted)',
                  fontSize: 'var(--vn-font-size-xs)',
                }}
              >
                ({alarm.severity})
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

